/**
 * @fileoverview Photos Timeline By Month - Vertical Timeline View
 *
 * Displays all photos in a continuous vertical timeline from top to bottom.
 * Photos sorted chronologically (newest → oldest) to show project evolution.
 * Optional month dividers for context, but no grouping/collapsing.
 *
 * @module RenovationProject/Components/PhotosTimelineByMonth
 */

import React, { useMemo, useState } from 'react';
import { FileLog } from 'shared/types/file';
import { Image as ImageIcon, Calendar } from '@phosphor-icons/react';
import { format, isSameMonth } from 'date-fns';
import { nl } from 'date-fns/locale';

interface PhotosTimelineByMonthProps {
  photos: FileLog[];
}

export const PhotosTimelineByMonth: React.FC<PhotosTimelineByMonthProps> = ({
  photos,
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState<FileLog | null>(null);

  // Sort photos chronologically (newest → oldest)
  const sortedPhotos = useMemo(() => {
    return [...photos].sort((a, b) => {
      const dateA = new Date(a.documentDate || a.createdAt || 0).getTime();
      const dateB = new Date(b.documentDate || b.createdAt || 0).getTime();
      return dateB - dateA; // Newest first for top-to-bottom timeline
    });
  }, [photos]);

  const getPhotoUrl = (photo: FileLog) => {
    if (photo.imageUrl) return photo.imageUrl;
    if (photo.sharePointFileUrl) return photo.sharePointFileUrl;
    return photo.s3Url || '/placeholder-image.jpg';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Geen datum';
    try {
      return format(new Date(dateString), 'd MMM yyyy', { locale: nl });
    } catch {
      return 'Ongeldige datum';
    }
  };

  const formatMonthYear = (date: Date) => {
    return format(date, 'MMMM yyyy', { locale: nl });
  };

  // Helper to determine if we should show a month divider
  const shouldShowMonthDivider = (currentPhoto: FileLog, previousPhoto: FileLog | null) => {
    if (!previousPhoto) return true; // Always show for first photo

    const currentDate = new Date(currentPhoto.documentDate || currentPhoto.createdAt || 0);
    const previousDate = new Date(previousPhoto.documentDate || previousPhoto.createdAt || 0);

    return !isSameMonth(currentDate, previousDate);
  };

  if (sortedPhotos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <ImageIcon size={48} weight="thin" className="mb-3 text-neutral-400" />
        <p className="text-neutral-600">Geen foto's beschikbaar</p>
        <p className="text-sm text-neutral-400">
          Selecteer projectonderdelen om foto's te zien
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Vertical Timeline Container */}
      <div className="relative pl-8">
        {/* Vertical Timeline Line */}
        <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-moss-primary via-moss-primary/50 to-moss-primary/20" />

        {/* Photos Grid */}
        <div className="grid grid-cols-3 gap-4 md:grid-cols-4 lg:grid-cols-5">
          {sortedPhotos.map((photo, index) => {
            const previousPhoto = index > 0 ? sortedPhotos[index - 1] : null;
            const showMonthDivider = shouldShowMonthDivider(photo, previousPhoto);
            const photoDate = new Date(photo.documentDate || photo.createdAt || 0);

            return (
              <React.Fragment key={photo.id}>
                {/* Month Divider */}
                {showMonthDivider && (
                  <div className="col-span-full relative -ml-8 mb-2 mt-4 flex items-center">
                    {/* Timeline Marker */}
                    <div className="absolute left-0 flex h-8 w-8 items-center justify-center rounded-full border-4 border-white bg-moss-primary shadow-md">
                      <Calendar size={14} weight="fill" className="text-white" />
                    </div>

                    {/* Month Label */}
                    <div className="ml-10 rounded-lg bg-moss-primary/10 px-4 py-2">
                      <span className="text-sm font-semibold capitalize text-moss-primary">
                        {formatMonthYear(photoDate)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Photo Card */}
                <div
                  className="group relative aspect-square cursor-pointer overflow-hidden rounded-lg bg-neutral-100 transition-all hover:scale-[1.02] hover:shadow-lg"
                  onClick={() => setSelectedPhoto(photo)}
                >
                  <img
                    src={getPhotoUrl(photo)}
                    alt={Array.isArray(photo.label) ? photo.label.join(', ') : (photo.label || 'Project photo')}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100">
                    <div className="absolute right-0 bottom-0 left-0 p-2">
                      <p className="truncate text-xs font-medium text-white">
                        {formatDate((photo.documentDate || photo.createdAt) as string)}
                      </p>
                      {photo.label && (
                        <p className="truncate text-xs text-white/80">
                          {Array.isArray(photo.label) ? photo.label.join(', ') : photo.label}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div
            className="relative max-h-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getPhotoUrl(selectedPhoto)}
              alt={Array.isArray(selectedPhoto.label) ? selectedPhoto.label.join(', ') : (selectedPhoto.label || 'Project photo')}
              className="max-h-[90vh] max-w-full rounded-lg object-contain"
            />
            <div className="absolute top-4 right-4 max-w-xs rounded-lg bg-white p-4 shadow-xl">
              <p className="text-neutral-primary mb-2 text-sm font-semibold">
                {formatDate((selectedPhoto.documentDate || selectedPhoto.createdAt) as string)}
              </p>
              {selectedPhoto.label && (
                <p className="text-neutral-secondary-mid mb-1 text-sm">
                  {selectedPhoto.label}
                </p>
              )}
              {selectedPhoto.summary && (
                <p className="text-neutral-secondary-mid mt-2 text-xs">
                  {selectedPhoto.summary}
                </p>
              )}
            </div>
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 left-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-neutral-primary transition-colors hover:bg-neutral-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};
